import React, { FunctionComponent, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import {
    Button,
    TextField,
    Typography,
    Box,
    CircularProgress,
    FormHelperText,
    FormControl
} from "@material-ui/core"
import { useAuth } from "../common/hooks/useAuth"
import styles from "./LoginForm.module.scss"
import { Title } from "../components/Title"
import { useQuery } from "@apollo/client"
import { ClientConfiguration } from "../../typings/types"
import { GET_CLIENT_CONFIGURATION } from "../queries/GET_CLIENT_CONFIGURATION"
import LoadingSpinnerStyles from "../common/LoadingSpinner.module.scss"
import { useNotification } from "../common/hooks/useNotification"

export const LoginForm: FunctionComponent = props => {
    const [emailInput, setEmailInput] = useState("")
    const [showEmailMessage, setShowEmailMessage] = useState(false)
    const [requestLoginLoading, setRequestLoginLoading] = useState(false)
    const [emailFieldError, setEmailFieldError] = useState("")
    const [reCaptchaSiteKey, setReCaptchaSiteKey] = useState("")
    const { notifyError } = useNotification()

    const { requestLogin } = useAuth()
    const recaptchaRef = useRef<ReCAPTCHA>(null)
    const { loading: configLoading, error: configError } = useQuery<{
        clientConfiguration: ClientConfiguration
    }>(GET_CLIENT_CONFIGURATION, {
        onCompleted: ({ clientConfiguration }) => {
            setReCaptchaSiteKey(clientConfiguration.reCaptchaSiteKey)
        },
        onError: err => {
            notifyError("Error connecting to server. Please try again.")
        }
    })

    const enableCaptcha = !configError && !(window as any).Cypress

    const submitForm = async (e: any) => {
        e.preventDefault()
        const recaptchaToken = (await recaptchaRef.current?.executeAsync()) ?? ""

        if (!emailInput) {
            setEmailFieldError("Required")
        } else {
            setRequestLoginLoading(true)
            requestLogin(emailInput, recaptchaToken)
                .then(() => {
                    setTimeout(() => {
                        setShowEmailMessage(true)
                        setRequestLoginLoading(false)
                    }, 800)
                })
                .catch(e => {
                    setRequestLoginLoading(false)
                    setEmailFieldError(e.message)
                })
        }
    }

    if (configLoading) {
        return (
            <div
                id="loading-spinner"
                className={LoadingSpinnerStyles.LoadingSpinner__absolute__center}
            >
                <CircularProgress />
            </div>
        )
    }

    return (
        <>
            <Title text={"Login"} />
            <div className={styles.LoginForm}>
                {showEmailMessage && (
                    <Box boxShadow={4} className={styles.LoginForm__email_card}>
                        <Typography>
                            Please check your inbox and open the email from the TTS Portal to
                            confirm your login.
                        </Typography>
                        <Typography>
                            If you did not receive an email, please contact support@versay.com for
                            assistance.
                        </Typography>
                    </Box>
                )}
                {!showEmailMessage && (
                    <form className={styles.LoginForm__content}>
                        <Typography
                            className={styles.LoginForm__content__element}
                            variant="h6"
                            data-testid="login-header"
                        >
                            Welcome to the TTS Portal
                        </Typography>
                        <FormControl>
                            <TextField
                                required
                                error={!!emailFieldError}
                                className={styles.LoginForm__content__element}
                                disabled={requestLoginLoading}
                                label="Email"
                                type="text"
                                value={emailInput}
                                id="email-box"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (emailFieldError) setEmailFieldError("")
                                    setEmailInput(e.target.value)
                                }}
                                inputProps={{ "data-testid": "login-email" }}
                            />

                            <FormHelperText
                                error={!!emailFieldError}
                                data-testid="email-required-error"
                            >
                                {!!emailFieldError ? emailFieldError : <br />}
                            </FormHelperText>
                        </FormControl>

                        <br />

                        <Button
                            id="login-btn"
                            className={styles.LoginForm__content__element}
                            disabled={requestLoginLoading || !emailInput || !!configError}
                            variant="contained"
                            color="primary"
                            value="Login"
                            type="submit"
                            onClick={submitForm}
                            data-testid="login-btn"
                            disableRipple
                        >
                            {requestLoginLoading && <CircularProgress size={"1.8em"} />}
                            {!requestLoginLoading && `Login`}
                        </Button>
                    </form>
                )}
            </div>
            <div id="captcha-tag" className={styles.LoginForm__captcha}>
                {enableCaptcha && (
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        badge="inline"
                        sitekey={reCaptchaSiteKey}
                    />
                )}
            </div>
        </>
    )
}
